import { useHistory, useLocation } from "react-router-dom";
import {
  useEffect,
  useState,
  useMemo,
  Fragment,
  useRef,
  createRef,
  useCallback,
} from "react";
import SummarySectionIndicator from "../components/global/LoadingIndicators/SummarySectionIndicator";
import { useOktaAuth } from "@okta/okta-react";
import toastMessages from "../services/utils/helpers/toastMessage";
import FileActions from "../services/utils/FileActions";
import Button from "../components/organism/Button";
import formatSingleOrderData from "../components/atomic/formatSingleOrderData";
import PDFPrint from "../components/global/PDFPrint";
import { useQueryClient } from "@tanstack/react-query";
import CustomPopUp from "../components/global/CustomPopup";
import CustomSignatureInput from "../components/global/CustomSignatureInput";
import useSignature from "../services/utils/custom hooks/eSignature/useSignature";
import useSendForApproval from "../services/utils/custom hooks/Approvals/useSendForApproval";
import ExistingSignaturePopup from "../components/popups/ExistingSignaturePopup";
import useFeatureFlags from "../services/utils/custom hooks/Feature Flagging/useFeatureFlags";
import dataConstants from "../constants/dataConstants";
import LeftArrow from "../assets/svg/LeftArrow";
import RightArrow from "../assets/svg/RightArrow";
import CustomText from "../components/atomic/CustomText";
import PrintSvg from "../assets/svg/PrintSvg";
import DownloadFileSvg from "../assets/svg/DownloadFileSvg";
import PatientInformation from "../components/molecular/PatientInformation";
import BillingInformation from "../components/molecular/BillingInformation";
import ClinicianInformation from "../components/molecular/ClinicianInformation";
import ClinicalInformation from "../components/molecular/ClinicalInformation";
import AdditionalInformation from "../components/molecular/AdditionalInformation";
import LaboratoryInfo from "../components/molecular/LaboratoryInfo";
import DocumentManagement from "../components/atomic/DocumentManagement";
import SectionsDropdown from "../components/molecular/SectionsDropdown";
import { handleAuthorization } from "../services/utils/helpers/handleAuthorization";
import SignatureOrderTexts from "../services/utils/helpers/signatureOrderTexts";
import ApprovalPopup from "../components/molecular/ApprovalPopup";
import DeclineEsopredictOrderPopup from "../components/popups/DeclineEsopredictOrderPopup";
import { useTestOrdersByIds } from "../services/utils/custom hooks/useTestOrdersById";
import formatApprovalDesktopData from "../components/atomic/formatApprovalDesktopData";
import ShipmentInformationReview from "../components/global/forms/OnlineOrderReviews/ShipmentInformationReview";
import GuarantorInfo from "../components/molecular/GuarantorInfo";
import getOrderTestDisplayLabel from "../utils/helpers/getOrderTestDisplayLabel";
import LoadingSvg from "../assets/svg/LoadingSVG";

type Section = {
  key: string;
  label: string;
  ref: React.RefObject<HTMLDivElement>;
  disabled?: boolean;
};

function OrderIndicatorAndActionsHeader({
  printOrder,
  handleOrderDownload,
  isPrinting,     // New prop
  isDownloading,  // New prop
}: {
  printOrder: Function;
  handleOrderDownload: Function;
  isPrinting: boolean;
  isDownloading: boolean;
}) {
  const history = useHistory();
  return (
    <div className="z-10 flex items-center justify-between py-4 px-0.5 bg-white sticky top-0">
      <button onClick={() => history.goBack()} className="flex cursor-pointer items-center">
        <LeftArrow color="#172B4D" className="mr-0.5" />
        <CustomText text="Back" fontWeight="font-semibold" fontSize="text-xs" fontColor="#172B4D" />
      </button>

      <CustomText text={"Authorize"} className="text-[#084E8B]" fontWeight="font-semibold" fontSize="text-sm" fontColor="#084E8B" />

      <div className="flex items-center gap-2">
        {/* Print Button/Loading */}
        {isPrinting ? (
          <LoadingSvg className="animate-spin" />
        ) : (
          <PrintSvg
            className="cursor-pointer"
            onClick={() => !isDownloading && printOrder()}
          />
        )}

        {/* Download Button/Loading */}
        {isDownloading ? (
          <LoadingSvg className="animate-spin" />
        ) : (
          <DownloadFileSvg
            className="cursor-pointer"
            onClick={() => !isPrinting && handleOrderDownload()}
          />
        )}
      </div>
    </div>
  );
}

function OrdersNavigation({
  navigatePatient,
  currentPatientIndex,
  currentPatient,
  totalPatients,
}: Readonly<{
  navigatePatient: Function;
  currentPatientIndex: number;
  currentPatient: any;
  totalPatients: number;
}>) {
  const testTitle = getOrderTestDisplayLabel(currentPatient, {
    useFullName: false,
  });
  const navLabel = `${currentPatientIndex + 1}. ${currentPatient?.patientName ?? ""} - ${testTitle}`;

  return (
    <div className="z-10 border-t border-b text-primary-foreground py-4 px-0.5 flex items-center justify-between sticky top-[56px] bg-white">
      <button
        onClick={() => navigatePatient("prev")}
        disabled={currentPatientIndex === 0}
        className=""
      >
        <LeftArrow className="h-5 w-5" />
      </button>

      <div className="text-center min-w-0 flex-1 px-1">
        <h1 className="text-lg font-semibold truncate" title={navLabel}>
          <CustomText
            text={navLabel}
            fontWeight="font-semibold"
            fontSize="text-sm"
            fontColor="#084E8B"
          />
        </h1>
        <CustomText
          text={`Order: ${currentPatientIndex + 1} of ${totalPatients}`}
          className="text-xs"
          fontWeight="font-medium"
          fontSize="text-[11px]"
          fontColor="#18B5B7"
        />
      </div>

      <button
        onClick={() => navigatePatient("next")}
        disabled={currentPatientIndex === totalPatients - 1}
        className=""
      >
        <RightArrow className="h-5 w-5" />
      </button>
    </div>
  );
}

function ApprovalsReviewMobile() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { isSectionActive, getActionsForSection } = useFeatureFlags();
  const ids = searchParams.get("ids")?.split(",");
  const history = useHistory();
  // const results = useTestOrders(ids);
  const {
    data: resultsByNewApi,
    isLoading: loadingOrderByIds,
    isError: errorOrderOfIds,
  } = useTestOrdersByIds({
    ids: ids ?? [],
    skipPagination: true,
  });
  // const loading = results?.some((result) => result?.isLoading);
  // const error = results?.some((result) => result?.isError);
  // const data = useMemo(() => results?.map((result) => result?.data), [results]);
  const newData = useMemo(
    () => resultsByNewApi?.data?.orders?.map((result) => result),
    [resultsByNewApi]
  );
  const fileActions = new FileActions();
  const { authState } = useOktaAuth();
  const [downloadingReport, setDownloadingReport] = useState<boolean>(false);
  const [orders, setOrders] = useState([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  // const testIds = data.map((e: any) => e?.data?.testTypes?.id);
  const testIdsNew = newData?.map((e: any) => e?.testTypes?.id);
  // const hasIdEight = testIds.includes(8);
  const hasIdEightNew = testIdsNew?.includes(8);
  const queryClient = useQueryClient();
  const [currentSection, setCurrentSection] = useState<string>("patient");
  const profile = queryClient.getQueryData<any>(["profile"]);
  const roles = profile?.user?.roles;
  const [selectedTabs, setSelectedTabs] = useState<any>({});
  const [multiPrinting, setMultiPrinting] = useState<boolean>(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [showSignaturePopup, setShowSignaturePopup] = useState<boolean>(false);
  const [showPreviewPopup, setShowPreviewPopup] = useState<boolean>(false);
  const { isLoading: fetchingSignature, data: signatureData } = useSignature();
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(false);
  const [signatureURL, setSignatureURL] = useState<string>("");
  const [noteVisible, setNoteVisible] = useState<boolean>(false);
  const { mutate, isPending } = useSendForApproval();
  const canAccess =
    isSectionActive(dataConstants.FeatureFlagSectionKeys.APPROVALS) &&
    getActionsForSection(dataConstants.FeatureFlagSectionKeys.APPROVALS).find(
      (action) => action.key === dataConstants.SectionActionKeys.AUTHORIZE_ORDER
    )?.isActive;
  const has6thTestTypeOrder = orders.some(
    (order: any) => order.testType.test === 8
  );
  const [currentPatientIndex, setCurrentPatientIndex] = useState(0);
  const currentPatient = newData?.map((e: any) => e)[currentPatientIndex];
  const totalPatients: any = newData?.length;

  const navigatePatient = (direction: "prev" | "next") => {
    let newIndex = currentPatientIndex;
    if (direction === "prev" && currentPatientIndex > 0) {
      newIndex = currentPatientIndex - 1;
    } else if (
      direction === "next" &&
      currentPatientIndex < totalPatients - 1
    ) {
      newIndex = currentPatientIndex + 1;
    }

    if (newIndex === currentPatientIndex) return;

    setCurrentSection("patient");
    setCurrentPatientIndex(newIndex);
    setTimeout(() => {
      const anchor = patientScrollAnchors.current[newIndex];
      anchor?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const [selectedIds, setSelectedIds] = useState<any>(
    newData?.map((e: any) => e?.id)
  );

  const selectedOrders: any[] = orders.filter((order: any) => {
    const found = selectedIds.find((orderId: number) => orderId == order.id);

    if (found) return true;
    return false;
  });
  // inside component
  const patientRefs = useRef<Array<HTMLDivElement | null>>([]);

  const perPatientSectionRefs = useRef<
    Record<
      number,
      {
        patientRef: React.RefObject<HTMLDivElement>;
        billingRef: React.RefObject<HTMLDivElement>;
        gurantorRef: React.RefObject<HTMLDivElement>;
        clinicianRef: React.RefObject<HTMLDivElement>;
        clinicalRef: React.RefObject<HTMLDivElement>;
        additionalRef: React.RefObject<HTMLDivElement>;
        labRef: React.RefObject<HTMLDivElement>;
        shipmentRef: React.RefObject<HTMLDivElement>;
        documentsRef: React.RefObject<HTMLDivElement>;
      }
    >
  >({});

  const getSectionRefsFor = (idx: number) => {
    if (!perPatientSectionRefs.current[idx]) {
      perPatientSectionRefs.current[idx] = {
        patientRef: createRef<HTMLDivElement>(),
        billingRef: createRef<HTMLDivElement>(),
        gurantorRef: createRef<HTMLDivElement>(),
        clinicianRef: createRef<HTMLDivElement>(),
        clinicalRef: createRef<HTMLDivElement>(),
        additionalRef: createRef<HTMLDivElement>(),
        shipmentRef: createRef<HTMLDivElement>(),
        labRef: createRef<HTMLDivElement>(),
        documentsRef: createRef<HTMLDivElement>(),
      };
    }
    return perPatientSectionRefs.current[idx];
  };
  const activeSectionPayload = useMemo(() => {
    if (!newData || !newData.length) return null;
    const order = newData[currentPatientIndex];
    const formatted = formatSingleOrderData(order);
    const patientData = newData?.map((e: any) => e)[currentPatientIndex];
    const {
      patientRef,
      billingRef,
      gurantorRef,
      clinicianRef,
      clinicalRef,
      additionalRef,
      shipmentRef,
      labRef,
      documentsRef,
    } = getSectionRefsFor(currentPatientIndex);
    const isTestType8 = patientData?.testTypes?.id === 8;

    const sections: Section[] = [
      { key: "patient", label: "Patient Information", ref: patientRef },
      { key: "billing", label: "Billing Information", ref: billingRef },
      { key: 'gurantor', label: 'Guarantor Information', ref: gurantorRef },
      { key: "clinician", label: "Clinician Information", ref: clinicianRef },
      ...(formatted?.testType?.test !== 1
        ? [
          {
            key: "clinical",
            label: isTestType8
              ? "Patient Healthcare Information"
              : "Clinical Information",
            ref: clinicalRef,
          },
        ]
        : []),
      ...(patientData?.additionalClinician?.length > 0
        ? [
          {
            key: "additional",
            label: "Additional Information",
            ref: additionalRef,
          },
        ]
        : []),
        
      { key: "shipment", label: "Shipment Information", ref: shipmentRef },
      {
        key: "laboratory",
        label: isTestType8 ? "Specimen Information" : "Laboratory Information",
        ref: labRef,
      },
      { key: "documents", label: "Supporting Documents", ref: documentsRef },
    ];

    return {
      sections,
      refs: {
        patientRef,
        billingRef,
        gurantorRef,
        clinicianRef,
        clinicalRef,
        additionalRef,
        shipmentRef,
        labRef,
        documentsRef,
      },
      formatted,
      order,
    };
  }, [currentPatientIndex, newData]);
  const hideManualSubmission =
    selectedOrders
      ?.map((order: any) => order?.orderStatus === "COA Order Attestation")
      ?.includes(true) && roles?.includes("Office Manager");
  const combinedLabels = useMemo(
    () =>
      newData
        ?.filter((order: any) => selectedIds?.includes(order?.id))
        .map((order: any) => ({
          patientName: order?.patientFirstName,
          testName: order?.testTypes?.limsTestName,
        })),
    [newData, selectedIds]
  );

  const pendingOrderAuthOrders = useMemo(
    () =>
      newData?.filter(
        (o: any) =>
          selectedIds?.includes(o?.id) &&
          o?.orderStatus === "Pending Order Authorization"
      ) ?? [],
    [newData, selectedIds]
  );

  const [showDeclinePopup, setShowDeclinePopup] = useState<boolean>(false);

  const patientScrollAnchors = useRef<Array<HTMLDivElement | null>>([]);
  const STICKY_HEADER_HEIGHT = 56 + 130; // adjust to your actual combined sticky header height
  const handleCheckboxChange = (id: string) => {
    setSelectedIds((prevSelectedIds: any) =>
      prevSelectedIds?.includes(id)
        ? prevSelectedIds.filter((selectedId: any) => selectedId !== id)
        : [...prevSelectedIds, id]
    );
  };
  // Near your other useState declarations
  const [printId, setPrintId] = useState<string[]>([]);

  // Add these memoized handlers
  const handlePrintStart = useCallback(() => {
    setMultiPrinting(true);
  }, []);

  const handlePrintError = useCallback((message: string) => {
    toastMessages.error(message);
    setMultiPrinting(false);
    setPrintId([]);
  }, []);

  const handlePrintSuccess = useCallback(() => {
    setMultiPrinting(false);
    setTimeout(() => {
      setPrintId([]);
    }, 500);
  }, []);

  function closePopup() {
    setIsPopupOpen(false);
  }

  function printOrder(): void {
    // Prevent click if already printing or downloading
    if (multiPrinting || downloadingReport) return;

    if (selectedIds && selectedIds.length > 0) {
      setPrintId(selectedIds.map((id: any) => String(id)));
    } else {
      toastMessages.error("No orders selected to print");
    }
  }

  async function handleOrderDownload() {
    if (downloadingReport || multiPrinting) return;
    if (!selectedIds || selectedIds.length === 0) {
      toastMessages.error("No orders selected to download");
      return;
    }
    setDownloadingReport(true);
    try {
      const payload: any = newData
        ?.filter((e: any) => selectedIds?.includes(e?.id))
        .map((e: any) => {
          let testName = !!e.testTypes?.name
            ? e.testTypes?.name.split(":")[0]
            : "";
          let fileName = `${e.id}_${!!e.patientFirstName ? `${e.patientFirstName} ` : "Patient"
            }_${testName}`;

          return {
            id: e.id,
            fileName: fileName,
          };
        });
      await fileActions.downloadMultiplePDF(
        payload,
        authState?.accessToken?.accessToken ?? ""
      );
    } catch (error: any) {
      toastMessages.error(
        error?.response?.data?.message ??
        error.message ??
        "Something went wrong"
      );
    } finally {
      setDownloadingReport(false);
    }
  }

  useEffect(() => {
    if (canAccess) {
      if (!loadingOrderByIds && newData && newData?.length > 0) {
        setOrders(formatApprovalDesktopData(newData));
      }
      setSelectedIds(newData?.map((e: any) => e?.id));
    } else if (!canAccess && roles?.length > 0) {
      history.push("/unauthorized");
    }
  }, [loadingOrderByIds]);

  // helper to observe visibility of each patient container
  useEffect(() => {
    if (!newData || newData?.length === 0) return;

    const stickyHeaderTotalHeight = 56 + 120; // adjust to your actual combined sticky header heights

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number((entry.target as HTMLElement).dataset.index);
            if (!isNaN(idx)) {
              setCurrentPatientIndex((prev) => (prev === idx ? prev : idx));
            }
          }
        });
      },
      {
        root: null,
        rootMargin: `${stickyHeaderTotalHeight}px 0px 0px 0px`,
        threshold: 0,
      }
    );

    patientRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    const orderSectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute("id") || "patient";
            if (
              localStorage.getItem("preventIntersectionObserver") === "false" ||
              !localStorage.getItem("preventIntersectionObserver")
            ) {
              setCurrentSection(id);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: `20px 0px 0px 0px`,
        threshold: 1,
      }
    );

    for (const idx of Object.keys(perPatientSectionRefs.current)) {
      const refsObj = perPatientSectionRefs.current[Number(idx)];

      if (refsObj) {
        Object.values(refsObj).forEach((ref) => {
          if (ref.current) {
            orderSectionObserver.observe(ref.current);
          }
        });
      }
    }

    return () => {
      observer.disconnect();
      orderSectionObserver.disconnect();
    };
  }, [newData]); // intentionally omit currentPatientIndex so observer isn’t torn down on index change

  return (
    <Fragment>
      {loadingOrderByIds ? (
        <SummarySectionIndicator />
      ) : (
        <div className="md:hidden flex flex-col overflow-auto z-10">
          <OrderIndicatorAndActionsHeader
            printOrder={printOrder}
            handleOrderDownload={handleOrderDownload}
            isPrinting={multiPrinting}
            isDownloading={downloadingReport}
          />
          <OrdersNavigation
            currentPatient={currentPatient}
            currentPatientIndex={currentPatientIndex}
            navigatePatient={navigatePatient}
            totalPatients={totalPatients}
          />
          <div className="z-10 border-t border-b flex items-center justify-between sticky top-[116px] bg-white">
            {activeSectionPayload && (
              <SectionsDropdown
                sections={activeSectionPayload.sections}
                current={currentSection}
                onSelect={(key) => {
                  const sec = activeSectionPayload.sections.find(
                    (s) => s.key === key
                  );
                  if (sec?.ref.current) {
                    sec.ref.current.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                    localStorage.setItem("preventIntersectionObserver", "true");
                    setCurrentSection(key);

                    setTimeout(
                      () =>
                        localStorage.setItem(
                          "preventIntersectionObserver",
                          "false"
                        ),
                      500
                    );
                  }
                }}
                key={currentPatientIndex} // reset internal if patient changes
              />
            )}
          </div>
          {newData?.map((order: any, idx: number) => {
            const formatted = formatSingleOrderData(order);
            const {
              patientRef,
              billingRef,
              gurantorRef,
              clinicianRef,
              clinicalRef,
              additionalRef,
              shipmentRef,
              labRef,
              documentsRef,
            } = getSectionRefsFor(idx);
            const checkboxId = `additionalClinician-${formatted?.id}`;
            const isChecked = selectedIds?.includes(formatted?.id);
            return (
              <div
                key={order?.data?.id}
                data-index={idx}
                ref={(el) => (patientRefs.current[idx] = el)}
                className="patient-block pb-8 px-3"
              >
                {/* checkbox/header etc. */}
                <div
                  ref={(el) => (patientScrollAnchors.current[idx] = el)}
                  style={{
                    paddingTop: `${STICKY_HEADER_HEIGHT}px`,
                    marginTop: `-${STICKY_HEADER_HEIGHT}px`,
                    pointerEvents: "none",
                  }}
                />

                <div
                  id="patient"
                  ref={patientRef}
                  className="pb-4 scroll-mt-[190px]"
                >
                  <label
                    htmlFor={checkboxId}
                    className="checkboxContainer block mt-2"
                  >
                    <input
                      id={checkboxId}
                      type="checkbox"
                      name="additionalClinician"
                      onChange={() => {
                        if (formatted?.id) handleCheckboxChange(formatted.id);
                      }}
                      checked={isChecked}
                      className="mr-2"
                    />
                    <span className="checkmark" />
                    <span>{`${idx + 1}. ${formatted.patientInfo.firstName} ${formatted.patientInfo.lastName} - ${formatted.header?.test ?? formatted.testType?.limsTestName ?? ""}`}</span>
                  </label>
                  <PatientInformation order={formatted} />
                </div>
                <div
                  ref={billingRef}
                  id="billing"
                  className="scroll-mt-[185px]"
                >
                  <BillingInformation order={formatted} />
                </div>
                <div
                  ref={gurantorRef}
                  id="gurantor"
                  className="scroll-mt-[185px]"
                >
                  <GuarantorInfo order={formatted} />
                </div>
                <div
                  ref={clinicianRef}
                  id="clinician"
                  className="scroll-mt-[185px]"
                >
                  <ClinicianInformation order={formatted} />
                </div>
                {formatted?.testType?.test !== 1 && (
                  <div
                    ref={clinicalRef}
                    id="clinical"
                    className="scroll-mt-[185px]"
                  >
                    <ClinicalInformation order={formatted} />
                  </div>
                )}
                {order?.additionalClinician?.length > 0 && (
                  <div
                    ref={additionalRef}
                    id="additional"
                    className="scroll-mt-[185px]"
                  >
                    <AdditionalInformation order={formatted} />
                  </div>
                )}
                   <div ref={shipmentRef} id="shipment" className="scroll-mt-[185px]">
        <CustomText
      text={"Shipment Information"}
      fontWeight="font-medium"
      className="reviewCardHeading my-5"
    />
    
      <ShipmentInformationReview values={order} />
      </div>
                <div ref={labRef} id="laboratory" className="scroll-mt-[185px]">
                  <LaboratoryInfo order={formatted} />
                </div>
                <div
                  id="documents"
                  ref={documentsRef}
                  className="scroll-mt-[185px]"
                >
                  <DocumentManagement
                    uploadedDocs={formatted?.supportingDocs}
                    values={formatted}
                    approval
                  />
                </div>
              </div>
            );
          })}
          {/* authorize / decline sticky bar */}
          <div
            className="fixed inset-x-0 bottom-3 z-50 px-4 pb-safe pt-2"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <div className="max-w-full mx-auto">
              <div
                className={`bg-white rounded-[100px] border border-gray-200 px-4 py-3 ${
                  pendingOrderAuthOrders.length > 0
                    ? "flex flex-row gap-2 items-center justify-between"
                    : "items-center justify-between"
                }`}
              >
                {pendingOrderAuthOrders.length > 0 && (
                  <Button
                    type="button"
                    className="h-[36px] flex-1 min-w-0 rounded-[999px] font-medium"
                    disabled={loadingOrderByIds}
                    textInformation={{
                      text: `Decline ${pendingOrderAuthOrders.length > 1 ? `(${pendingOrderAuthOrders.length})` : ""}`,
                      fontWeight: "font-semibold",
                    }}
                    buttonTheme="secondary"
                    onClick={() => setShowDeclinePopup(true)}
                  />
                )}
                <Button
                  type="button"
                  className={`h-[36px] rounded-[999px] bg-primary hover:bg-primary/90 text-primary-foreground font-medium ${
                    pendingOrderAuthOrders.length > 0 ? "flex-1 w-auto" : "w-full"
                  }`}
                  disabled={selectedIds?.length === 0}
                  textInformation={{
                    text: `Authorize ${selectedIds?.length > 0 ? `(${selectedIds?.length})` : ""}`,
                    fontWeight: "font-semibold",
                  }}
                  buttonTheme="primary"
                  onClick={() =>
                    handleAuthorization({
                      orders,
                      profile,
                      roles,
                      signatureData,
                      setShowSignaturePopup,
                      setShowPreviewPopup,
                      setNoteVisible,
                      setIsPopupOpen,
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Order */}
      <PDFPrint
        onStart={handlePrintStart}
        onError={handlePrintError}
        onSuccess={handlePrintSuccess}
        ids={printId}
      />

      {/* Decline Esopredict Order Popup */}
      {showDeclinePopup && (
        <DeclineEsopredictOrderPopup
          orders={pendingOrderAuthOrders.map((o: any) => ({
            id: o.id,
            patientFirstName: o.patientFirstName,
            patientLastName: o.patientLastName,
          }))}
          onClose={() => setShowDeclinePopup(false)}
          onDeclineSuccess={() => {
            setShowDeclinePopup(false);
            history.goBack();
          }}
        />
      )}
      {/* Approval Popup */}
      {isPopupOpen && (
        <div className="fixed inset-0 z-[2000000] flex items-center justify-center bg-modalBackdrop overflow-hidden">
          <ApprovalPopup
            ids={selectedIds}
            closePopup={closePopup}
            submitting={submitting}
            downloadOrders={handleOrderDownload}
            hasIdg={hasIdEightNew}
            downloading={downloadingReport}
            hideManualSubmission={hideManualSubmission}
            labels={combinedLabels}
            isMobileTable
          />
        </div>
      )}
      {showSignaturePopup && (
        <CustomPopUp
          popUpTitle="E-Signature"
          popUpSubtitle="E-signature is required for order authorization"
          hidePopUp={() => setShowSignaturePopup(false)}
        >
          <SignatureOrderTexts
            data={selectedOrders}
            roles={roles}
            className=""
          />
          <CustomSignatureInput
            submitting={isPending}
            handleSubmission={(signatureUrl) => {
              if (has6thTestTypeOrder) {
                setSignatureURL(signatureUrl);
                setShowSignaturePopup(false);
                setShowDisclaimer(true);

                return;
              }

              mutate(
                {
                  username: "",
                  password: "",
                  orderIds: selectedIds,
                  file: {
                    fileName: `${profile?.user?.id}_${profile?.user?.firstName}_signature_file.png`,
                    base64url: signatureUrl,
                  },
                },
                {
                  onSuccess: () => {
                    history.push("/access/approvals/order-authorized");
                  },
                }
              );
            }}
          />
        </CustomPopUp>
      )}
      {showPreviewPopup && (
        <CustomPopUp
          popUpTitle="E-Signature"
          popUpSubtitle=" This signature will be used for order authorizations"
          subTitleClassName="before:content-['Note:'] before:text-requiredRed before:font-semibold"
          hidePopUp={() => setShowPreviewPopup(false)}
        >
          <SignatureOrderTexts
            data={selectedOrders}
            roles={roles}
            className=""
          />
          <ExistingSignaturePopup
            imageURL={signatureData?.viewableURL}
            // imageURL="https://plus.unsplash.com/premium_photo-1661482843558-f16df35e432c?q=80&w=1954&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            handleChange={() => {
              setShowPreviewPopup(false);
              setShowSignaturePopup(true);
            }}
            buttonTitle={has6thTestTypeOrder ? "Next" : "Done"}
            submitting={isPending}
            handleDone={() => {
              if (has6thTestTypeOrder) {
                setShowPreviewPopup(false);
                setShowDisclaimer(true);
                return;
              }

              mutate(
                {
                  username: "",
                  password: "",
                  orderIds: selectedIds,
                  eSignatureId: signatureData?.id,
                },
                {
                  onSuccess: () => {
                    setShowPreviewPopup(false);
                    history.push("/access/approvals/order-authorized");
                  },
                }
              );
            }}
          />
        </CustomPopUp>
      )}
      {showDisclaimer && (
        <CustomPopUp
          popUpTitle=""
          popUpSubtitle=" "
          hidePopUp={() => setShowDisclaimer(false)}
        >
          <div className="border border-roundedInputBorder p-4 rounded-md bg-sideNavGray my-3 max-w-[50vw]">
            <h3 className="font-semibold !text-sm text-royalBlue">
              Disclaimer
            </h3>
            <p className="text-royalBlue text-xs mt-5">
              By my signature below, I, the healthcare provider, authorize
              performance of NeuroIDgenetix 902 and indicate that the purpose of
              the test(s), the procedures, the benefits and risks involved have
              been explained to the patient and patient informed consent has
              been obtained in accordance with state and local laws. I attest
              that this patient has a diagnosis for which pharmacologic therapy
              is reasonable and necessary, and that the drug or drugs that I
              intend to prescribe are reasonable and necessary for the treatment
              of this patient's diagnosis. I have made an initial personalized
              decision for this patient based on the patient's non-genetic
              factors. I have a record of what drug(s) is/are being considered
              and for what indication(s) to ensure the test(s) performed is
              reasonable and necessary. The test results will be utilized by me
              to determine a dose change to the patient's current medication
              and/or prescribe a new medication. I have informed the patient
              that Castle Biosciences, Inc. may be an out-of-network provider
              under their insurance plan and that I do not have any financial
              interest in Castle Biosciences, Inc.
            </p>
          </div>
          <Button
            buttonTheme="primary"
            disabled={isPending}
            loading={isPending}
            textInformation={{
              text: "Agree & Submit",
              fontWeight: "font-semibold",
            }}
            onClick={() => {
              const navOnSuccess = () => {
                history.push("/access/approvals/order-authorized");
              };
              if (!!signatureURL) {
                mutate(
                  {
                    username: "",
                    password: "",
                    orderIds: selectedIds,
                    file: {
                      fileName: `${profile?.user?.id}_${profile?.user?.firstName}_signature_file.png`,
                      base64url: !!signatureURL ? signatureURL : "",
                    },
                  },
                  { onSuccess: navOnSuccess }
                );
              }
              mutate(
                {
                  username: "",
                  password: "",
                  orderIds: selectedIds,
                  eSignatureId: !!signatureData?.id ? signatureData.id : "",
                },
                { onSuccess: navOnSuccess }
              );
            }}
          />
        </CustomPopUp>
      )}
      {noteVisible && (
        <CustomPopUp
          popUpTitle="E-Signature"
          popUpSubtitle=" Are you sure you want to authorize the following orders with physician credentials?"
          subTitleClassName="before:content-['Note:'] before:text-requiredRed before:font-semibold"
          hidePopUp={() => setNoteVisible(false)}
        >
          <SignatureOrderTexts
            data={selectedOrders}
            roles={roles}
            className="!w-full"
          />
          <div className="flex w-full justify-end items-center">
            <Button
              buttonTheme="secondary"
              disabled={false}
              loading={false}
              className="mx-3"
              textInformation={{
                text: "No",
                fontWeight: "font-semibold",
              }}
              onClick={() => {
                setNoteVisible(false);
              }}
            />
            <Button
              buttonTheme="primary"
              disabled={false}
              loading={false}
              textInformation={{
                text: "Yes",
                fontWeight: "font-semibold",
              }}
              onClick={() => {
                setNoteVisible(false);
                setIsPopupOpen(true);
              }}
            />
          </div>
        </CustomPopUp>
      )}
    </Fragment>
  );
}

export default ApprovalsReviewMobile;
